import { useTranslation } from "react-i18next";

import type { GenreNodeView } from "../../services/generated/client.js";
import { cn } from "../../lib/utils.js";

/**
 * What a party plays from — phase 16h.
 *
 * # Grouping is this file's job, and that is the whole design
 *
 * `parties.deck_id` was a single FK, so *"eighties and nineties"* was not
 * expressible. Decided with the maintainer: **the backend holds data — genre ids
 * and decades — and the UI groups it.** So there is no "deck" here, only two
 * lists of things you can turn on, and a deck is whatever combination somebody
 * happens to pick.
 *
 * # ⚠️ Nothing selected means everything
 *
 * Not "nothing". A party that has chosen no genres draws from every genre, which
 * is the honest reading of an untouched picker and is what makes a party
 * playable before anybody configures it — where a `NULL` deck used to be a hard
 * refusal to start.
 *
 * # The thin-selection warning is more useful here, not less
 *
 * `DeckPicker` warned when a deck was too thin to start a game. The same warning
 * matters more now, because **unioning is precisely how a host fixes it**: add a
 * decade and the count goes up, which the old picker could not offer.
 *
 * # ⚠️ It lives in `components/game/` because three routes render it
 *
 * The lobby, `/setup` and `/admin` all ask the same question at different
 * levels (phase 16g-2), and a picker that only the lobby could render would have
 * been copied twice. It takes props and gives back a callback — it does not know
 * whether it is choosing for a party or for the whole installation.
 */
export function SelectionPicker({
  genres,
  decades,
  chosenGenres,
  chosenDecades,
  playableTracks,
  wildcardTracks,
  disabled = false,
  onChange,
}: {
  /** The whole tree, families and subgenres. `null` while loading. */
  genres: GenreNodeView[] | null;
  /**
   * The decades on offer, as the first year of each.
   *
   * From the server since 16g-2, because an installation may serve only some of
   * them. `null` while loading falls back to the full list, so the picker still
   * renders something rather than an empty row.
   */
  decades?: readonly number[] | null;
  chosenGenres: readonly string[];
  chosenDecades: readonly number[];
  /**
   * How many tracks the current selection admits, from the server.
   *
   * **Negative means nobody has asked**, which is `/setup`'s case: it runs
   * before there is an admin, so nothing is authorised to count. The line is
   * hidden rather than showing a wrong or alarming zero.
   *
   * In a lobby this is the **on-brief** count and {@link wildcardTracks}
   * carries the rest; on `/setup` and `/admin` there is no party, so there are
   * no wildcards and this is simply the catalog count.
   */
  playableTracks: number;
  /**
   * How many of a party's cards sit **outside what the host asked for** — phase
   * 19e-4, ADR 0025.
   *
   * `undefined` for the two installation-level callers, which have no party and
   * therefore no wildcards; the split is not drawn for them at all rather than
   * being drawn as zero, because "this concept does not apply here" and "there
   * are none of them" are different things to show a person.
   *
   * 🔴 **Why it is not folded into a total.** A wildcard is rationed by
   * `wildcards_per_ten`, so a deck of a thousand wildcards and four on-brief
   * cards is a game that runs out after four rounds — and a sum reports it as
   * healthy. It is shown here, beside the genre chips, because narrowing genres
   * is what *causes* it: an unresolved Spotify recording has no genre to test.
   */
  wildcardTracks?: number;
  disabled?: boolean;
  onChange: (next: { genres: string[]; decades: number[] }) => void;
}) {
  const { t } = useTranslation();

  const toggleGenre = (id: string) => {
    const next = chosenGenres.includes(id)
      ? chosenGenres.filter((held) => held !== id)
      : [...chosenGenres, id];
    onChange({ genres: next, decades: [...chosenDecades] });
  };

  const toggleDecade = (decade: number) => {
    const next = chosenDecades.includes(decade)
      ? chosenDecades.filter((held) => held !== decade)
      : [...chosenDecades, decade];
    onChange({ genres: [...chosenGenres], decades: next });
  };

  // Families only at the top level: picking one takes its subgenres with it,
  // server-side, so offering both would be offering the same choice twice.
  const families = genres?.filter((genre) => genre.parent_id === null) ?? null;
  const unconstrained = chosenGenres.length === 0 && chosenDecades.length === 0;

  return (
    <section aria-label={t("selection.title")} data-testid="selection-picker">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-white/50">
        {t("selection.title")}
      </h2>
      <p className="mb-3 text-xs text-white/40">{t("selection.lead")}</p>

      <h3 className="mb-1 text-xs font-medium text-white/60">{t("selection.decades")}</h3>
      <div className="mb-3 flex flex-wrap gap-1.5">
        {(decades ?? DECADES).map((decade) => (
          <Chip
            key={decade}
            label={`${decade}s`}
            on={chosenDecades.includes(decade)}
            disabled={disabled}
            onClick={() => {
              toggleDecade(decade);
            }}
          />
        ))}
      </div>

      <h3 className="mb-1 text-xs font-medium text-white/60">{t("selection.genres")}</h3>
      {families === null ? (
        <p className="text-xs text-white/40">{t("selection.loading")}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {families.map((family) => (
            <Chip
              key={family.id}
              label={family.name}
              on={chosenGenres.includes(family.id)}
              disabled={disabled}
              onClick={() => {
                toggleGenre(family.id);
              }}
            />
          ))}
        </div>
      )}

      <p
        className={cn(
          "mt-3 text-xs tabular-nums",
          playableTracks === 0 ? "text-wrong-500" : "text-white/50",
        )}
      >
        {unconstrained && (
          <span className="mr-2 uppercase tracking-wide text-flash-500">
            {t("selection.everything")}
          </span>
        )}
        {playableTracks >= 0 && t("selection.count", { count: playableTracks })}
      </p>
      {/* The wildcards, on their own line and never added to the one above.
          Drawn only when there are some: a party with no Spotify sources and no
          undated cards has nothing to explain, and a permanent "0 wildcards"
          would be a sentence about a mechanic most parties never meet. */}
      {wildcardTracks !== undefined && wildcardTracks > 0 && (
        <p className="mt-1 text-xs tabular-nums text-white/40">
          {t("selection.wildcards", { count: wildcardTracks })}
        </p>
      )}
      {playableTracks === 0 && (
        <p role="alert" className="mt-1 text-xs text-wrong-500">
          {/* ⚠️ A different sentence when the deck is *only* wildcards, because
              the fix is different: widen the genres, or allow more wildcards per
              ten. Telling that host to "widen the selection" describes what they
              already did. */}
          {wildcardTracks !== undefined && wildcardTracks > 0
            ? t("selection.onlyWildcards")
            : t("selection.empty")}
        </p>
      )}
      {playableTracks > 0 && playableTracks < THIN && (
        <p className="mt-1 text-xs text-coin-400">{t("selection.thin")}</p>
      )}
    </section>
  );
}

/**
 * Every decade the game has ever offered.
 *
 * The fallback for a picker that has not heard from the server yet, and the
 * mirror of `catalog::selection::ALL_DECADES`. Two lists, because the picker
 * renders before it has asked anything; the server's is authoritative and
 * narrows this one to what the installation serves.
 */
const DECADES = [1950, 1960, 1970, 1980, 1990, 2000, 2010, 2020] as const;

/**
 * Below this a selection is worth warning about.
 *
 * The same 60 as `catalog::selection::THIN_SELECTION_THRESHOLD`, one on each
 * side of the wire: a game deals a card per round per side, and a party that
 * runs out mid-game is the failure this prevents.
 */
const THIN = 60;

/** One toggle. */
function Chip({
  label,
  on,
  disabled,
  onClick,
}: {
  label: string;
  on: boolean;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={on}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "min-h-9 rounded-full border px-3 text-xs font-medium transition-colors disabled:opacity-40",
        on
          ? "border-flash-500 bg-flash-500/20 text-white"
          : "border-white/20 text-white/70 hover:border-white/40",
      )}
    >
      {label}
    </button>
  );
}
